import { AppointmentResourceType, AppointmentStatus } from 'constants/'
import { useMemo } from 'react'
import { CustomerAppointment, Resource } from 'types'
import { useDashboardAppointments } from '../'

interface UseAppointmentDisplayStatusProps {
  appointment?: CustomerAppointment
  customerGid?: string
}

interface UseAppointmentDisplayStatusResult {
  displayStatus: AppointmentStatus | undefined
  isLoading: boolean
}

const resourceTypesOfInterest = new Set([
  AppointmentResourceType.FITTER,
  AppointmentResourceType.FITTER_INTERNAL,
  AppointmentResourceType.VIDEO_CARE_CALL_SPECIALIST,
])

const isResourceOfInterest = (resource: Resource) => resourceTypesOfInterest.has(resource.type)

const useAppointmentDisplayStatus = ({
  appointment,
  customerGid,
}: UseAppointmentDisplayStatusProps): UseAppointmentDisplayStatusResult => {
  const originalStatus = appointment?.status?.status

  const shouldFetchAppointments = originalStatus === AppointmentStatus.RESCHEDULED && Boolean(customerGid)

  const { data: customerAppointments = [], isLoading: isLoadingAppointments } = useDashboardAppointments(
    {
      customerGid,
      statuses: [AppointmentStatus.CONFIRMED, AppointmentStatus.COMPLETED],
    },
    {
      enabled: shouldFetchAppointments,
    }
  )

  const calculatedDisplayStatus = useMemo(() => {
    if (!appointment || !originalStatus) {
      return undefined
    }

    if (originalStatus !== AppointmentStatus.RESCHEDULED) {
      return originalStatus
    }

    if (isLoadingAppointments) {
      return undefined
    }

    const currentAppointmentResource = appointment.resources?.find(isResourceOfInterest)

    if (!currentAppointmentResource || !customerAppointments.length) {
      return originalStatus
    }

    const relevantAppointmentsOfSameType = customerAppointments.filter(
      (otherAppointment) => otherAppointment.type.appointment_type === appointment.type.appointment_type
    )

    if (!relevantAppointmentsOfSameType.length) {
      return originalStatus
    }

    let hasResourceMismatch = false

    for (const otherAppointment of relevantAppointmentsOfSameType) {
      const otherResource = otherAppointment.resources?.find(isResourceOfInterest)

      if (otherResource && otherResource.id !== currentAppointmentResource.id) {
        hasResourceMismatch = true
        break
      }
    }

    return hasResourceMismatch ? AppointmentStatus.CANCELED : originalStatus
  }, [appointment, originalStatus, customerAppointments, isLoadingAppointments])

  const isLoading = originalStatus === AppointmentStatus.RESCHEDULED && isLoadingAppointments

  return useMemo(
    () => ({
      displayStatus: calculatedDisplayStatus,
      isLoading,
    }),
    [calculatedDisplayStatus, isLoading]
  )
}

export { useAppointmentDisplayStatus }
